// File: frontend/src/pages/JoinV2/JoinV2.js

import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import JobApplicationForm from '../../components/JobApplicationForm/JobApplicationForm';
import { FiUsers, FiStar, FiTrendingUp, FiGlobe, FiAward, FiHeart } from 'react-icons/fi';
import styles from './JoinV2.module.css';

// Import the LeaderProfile component
import LeaderProfile from '../../components/LeaderProfile/LeaderProfile';

const JoinV2 = () => {
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState('about');

  const positions = {
    actuarial_science: 'positions.actuarial_science',
    risk_management: 'positions.risk_management',
    risk_related_asset_management: 'positions.risk_related_asset_management',
    data_science: 'positions.data_science',
    software_engineering: 'positions.software_engineering',
    ai_ml_research: 'positions.ai_ml_research',
    physics: 'positions.physics',
    mathematics: 'positions.mathematics',
    economics: 'positions.economics',
    revenue_leadership: 'positions.revenue_leadership'
  };

  const benefits = [
    {
      icon: <FiStar />,
      title: t('join.benefits.competitive_title', 'Competitive Compensation'),
      description: t('join.benefits.competitive_desc', 'Attractive base salary with equity options and performance bonuses')
    },
    {
      icon: <FiGlobe />,
      title: t('join.benefits.remote_title', 'Remote-First Culture'),
      description: t('join.benefits.remote_desc', 'Work from anywhere with flexible hours and location independence')
    },
    {
      icon: <FiTrendingUp />,
      title: t('join.benefits.growth_title', 'Growth Opportunities'),
      description: t('join.benefits.growth_desc', 'Continuous learning, mentorship, and clear paths for advancement')
    },
    {
      icon: <FiUsers />,
      title: t('join.benefits.team_title', 'Exceptional Team'),
      description: t('join.benefits.team_desc', 'Collaborate with talented professionals who are leaders in their fields')
    },
    {
      icon: <FiAward />,
      title: t('join.benefits.impact_title', 'Meaningful Impact'),
      description: t('join.benefits.impact_desc', 'Help transform the insurance industry and make a difference in people\'s lives')
    },
    {
      icon: <FiHeart />,
      title: t('join.benefits.health_title', 'Health & Wellness'),
      description: t('join.benefits.health_desc', 'Comprehensive health benefits and wellness programs to support your wellbeing')
    }
  ];

  // Founder profile data
  const founderHighlights = [
    {
      icon: "Award",
      title: t('investor_pitch.leadership.highlights.actuary', 'Fellow of the Society of Actuaries'),
      color: "var(--color-blue-310)"
    },
    // {
    //   icon: "GraduationCap",
    //   title: t('investor_pitch.leadership.highlights.education', 'MSc in Actuarial Science'),
    //   color: "var(--color-green-310)"
    // },
    // {
    //   icon: "Medal",
    //   title: t('investor_pitch.leadership.highlights.military', 'Military Officer'),
    //   color: "var(--color-red-310)"
    // },
    {
      icon: "Building2",
      title: t('investor_pitch.leadership.highlights.founder', 'Serial Entrepreneur'),
      color: "var(--color-orange-310)"
    }
  ];

  const founderAchievements = [
    t('investor_pitch.leadership.achievements.telematics', { customers: '6M+' }, 'Built telematics platform serving 6M+ customers'),
    // t('investor_pitch.leadership.achievements.funding', { amount: '$50M+', team: '6', total: '40' }, 'Raised $50M+ with team of 6, grew to 40'),
    t('investor_pitch.leadership.achievements.expertise', 'Deep expertise in insurance technology and AI')
  ];

  const founderSocialLinks = {
    linkedin: "https://www.linkedin.com/in/onur-gungor/",
    x: "https://x.com/_onurgungor",
    github: "https://github.com/toolsallegory"
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <div className={styles.heroSection}>
          <h1 className={styles.title}>{t('join_allegory')}</h1>
          <div className={styles.titleUnderline}></div>
          <p className={styles.subtitle}>
            {t('join.subtitle', 'Join our team of innovators and help us transform the future of insurance')}
          </p>
        </div>

        <div className={styles.navigationTabs}>
          <button 
            className={`${styles.navTab} ${activeSection === 'about' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('about')}
          >
            {t('join.tabs.about', 'About Us')}
          </button>
          <button 
            className={`${styles.navTab} ${activeSection === 'positions' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('positions')}
          >
            {t('join.tabs.positions', 'Open Positions')}
          </button>
          <button 
            className={`${styles.navTab} ${activeSection === 'benefits' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('benefits')}
          >
            {t('join.tabs.benefits', 'Benefits')}
          </button>
          <button 
            className={`${styles.navTab} ${activeSection === 'apply' ? styles.activeTab : ''}`}
            onClick={() => setActiveSection('apply')}
          >
            {t('join.tabs.apply', 'Apply Now')}
          </button>
        </div>

        {activeSection === 'about' && (
        <section className={styles.aboutSection}>
            <div className={styles.sectionHeader}>
            <h2>{t('join.about.title', 'Our Mission')}</h2>
            <div className={styles.underline}></div>
            </div>
            
            <div className={styles.aboutContent}>
            <div className={styles.missionText}>
                <p className={styles.paragraph}>{t('join_intro_1')}</p>
                <p className={styles.paragraph}>{t('join_intro_1_cont')}</p>
                <p className={styles.paragraph}>{t('join_intro_2')}</p>
                <p className={styles.paragraph}>{t('join_intro_2_cont')}</p>
            </div>
            
            <div className={styles.visionStatement}>
                <blockquote className={styles.visionQuote}>
                {t('join.about.vision_quote')}
                </blockquote>
            </div>
            
            <div className={styles.founderSection}>
                <div className={styles.sectionDivider}>
                <span>{t('join.about.leadership', 'Leadership')}</span>
                </div>
                
                <h3 className={styles.founderHeading}>{t('join.about.founder', 'Meet Our Founder')}</h3>
                
                <div className={styles.founderProfile}>
                <LeaderProfile
                    name={t('investor_pitch.leadership.profile.name', 'Onur Gungor')}
                    role={t('investor_pitch.leadership.profile.role', 'Founder & CEO')}
                    highlights={founderHighlights}
                    achievements={founderAchievements}
                    socialLinks={founderSocialLinks}
                    photoUrl="/images/onurgungor2-v1.jpg"
                />
                </div>
            </div>
            
            <div className={styles.ctaContainer}>
                <button 
                className={styles.ctaButton}
                onClick={() => setActiveSection('apply')}
                >
                {t('join.about.cta', 'Join Our Team')}
                </button>
            </div>
            </div>
        </section>
        )}

        {activeSection === 'positions' && (
          <section className={styles.positionsSection}>
            <div className={styles.sectionHeader}>
              <h2>{t('looking_for')}</h2>
              <div className={styles.underline}></div>
            </div>
            
            <p className={styles.positionsIntro}>
              {/* {t('join.positions.intro', 'We\'re looking for exceptional talent in the following areas:')} */}
            </p>
            
            <div className={styles.positionsGrid}>
              {Object.entries(positions).map(([key, value]) => (
                <div key={key} className={styles.positionCard}>
                  <h3 className={styles.positionTitle}>{t(value)}</h3>
                  <p className={styles.positionDescription}>
                    {t(`join.positions.descriptions.${key}`, 'Join our team of experts and help shape the future of insurance technology.')}
                  </p>
                  <button 
                    className={styles.applyButton}
                    onClick={() => setActiveSection('apply')}
                  >
                    {t('join.positions.apply_button', 'Apply Now')}
                  </button>
                </div>
              ))}
            </div>
            
            <div className={styles.positionsNote}>
              <p>
                {t('join.positions.note', 'Don\'t see a perfect match? We\'re always looking for exceptional talent. Apply with your specific expertise and let us know how you can contribute to our mission.')}
              </p>
            </div>
          </section>
        )}

        {activeSection === 'benefits' && (
          <section className={styles.benefitsSection}>
            <div className={styles.sectionHeader}>
              <h2>{t('join.benefits.title', 'Why Join Allegory')}</h2>
              <div className={styles.underline}></div>
            </div>
            
            <p className={styles.benefitsIntro}>
              {t('join.benefits.intro', 'At Allegory, we believe in taking care of our team. Here\'s what you can expect when you join us:')}
            </p>
            
            <div className={styles.benefitsGrid}>
              {benefits.map((benefit, index) => (
                <div key={index} className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>{benefit.icon}</div>
                  <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                  <p className={styles.benefitDescription}>{benefit.description}</p>
                </div>
              ))}
            </div>
            
            <div className={styles.offerDetails}>
              <h3>{t('our_offer')}</h3>
              <ul className={styles.offerList}>
                <li>{t('offer.base_salary')}</li>
                <li>{t('offer.locations')}</li>
                <li>{t('offer.equity')}</li>
                <li>{t('offer.collaborative_environment')}</li>
                <li>{t('offer.open_to_discussion')}</li>
              </ul>
            </div>
            
            <div className={styles.ctaContainer}>
              <button 
                className={styles.ctaButton}
                onClick={() => setActiveSection('apply')}
              >
                {t('join.benefits.cta', 'Apply Today')}
              </button>
            </div>
          </section>
        )}

        {activeSection === 'apply' && (
          <section className={styles.applySection}>
            <div className={styles.sectionHeader}>
              <h2>{t('join.apply.title', 'Application Process')}</h2>
              <div className={styles.underline}></div>
            </div>
            
            <div className={styles.applicationProcess}>
              <div className={styles.processStep}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepContent}>
                  <h3>{t('join.apply.step1_title', 'Submit Your Application')}</h3>
                  <p>{t('join.apply.step1_desc', 'Complete the form below with your information and upload your resume and cover letter.')}</p>
                </div>
              </div>
              
              <div className={styles.processStep}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepContent}>
                  <h3>{t('join.apply.step2_title', 'Initial Review')}</h3>
                  <p>{t('join.apply.step2_desc', 'Our team will review your application and reach out if there\'s a potential match.')}</p>
                </div>
              </div>
              
              <div className={styles.processStep}>
                <div className={styles.stepNumber}>3</div>
                <div className={styles.stepContent}>
                  <h3>{t('join.apply.step3_title', 'Interview Process')}</h3>
                  <p>{t('join.apply.step3_desc', 'You\'ll meet with team members through a series of interviews to assess mutual fit.')}</p>
                </div>
              </div>
              
              <div className={styles.processStep}>
                <div className={styles.stepNumber}>4</div>
                <div className={styles.stepContent}>
                  <h3>{t('join.apply.step4_title', 'Welcome to Allegory')}</h3>
                  <p>{t('join.apply.step4_desc', 'If selected, you\'ll receive an offer and begin your journey with our team.')}</p>
                </div>
              </div>
            </div>
            
            <div className={styles.applicationRequirements}>
              <h3>{t('application_requirements')}</h3>
              <p>{t('join.apply.requirements_intro', 'Please prepare the following for your application:')}</p>
              <ul className={styles.requirementsList}>
                <li>{t('applicationRequirements.resume')}</li>
                <li>
                  {t('applicationRequirements.cover_letter')}
                  <ul className={styles.subRequirementsList}>
                    <li>{t('applicationRequirements.cover_letter.reasoning_mastery')}</li>
                    <li>{t('applicationRequirements.cover_letter.professional_case_studies')}</li>
                    <li>{t('applicationRequirements.cover_letter.unique_narrative')}</li>
                  </ul>
                </li>
              </ul>
            </div>
            
            <div className={styles.applicationFormContainer}>
              <h3>{t('jobApplication.title')}</h3>
              <JobApplicationForm />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default JoinV2;